import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { getApiUrl } from '../../utils/apiUrl';
import { SELECTED_APP_ID_KEY } from '../shared/SelectAppPage';

const MAX_SINGLE_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_TOTAL_ATTACHMENT_BYTES = 20 * 1024 * 1024; // 20MB

const getDefaultDateTimeLocal = () => {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

const toDateTimeLocalValue = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
  } catch (_) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
};

const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const getAttachmentIcon = (mimeType) => {
  const mt = String(mimeType || '');
  if (mt.startsWith('image/')) return <ImageIcon sx={{ fontSize: 18 }} />;
  return <AttachFileIcon sx={{ fontSize: 18 }} />;
};

const NotificationFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useApp();
  const isCreate = !id;
  const notificationFromState = location.state?.notification;

  const announcementsUrl = getApiUrl('/api/clients/announcements/');
  const attachmentsUrl = getApiUrl('/api/clients/announcement-attachments/');
  const appsUrl = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
  const selectedAppId = typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;

  const [formData, setFormData] = useState(() => {
    const defaultDt = getDefaultDateTimeLocal();
    return {
      type: '',
      message: '',
      operation_start: defaultDt,
      operation_end: defaultDt,
      display_start: defaultDt,
      display_end: defaultDt,
      attachments: [],
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState({ severity: 'info', title: '', text: '' });
  const [apps, setApps] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [authVerifyUser, setAuthVerifyUser] = useState(null);

  const allowedTypes = useMemo(() => {
    if (currentUser?.department === 'water') return ['water_shutdown'];
    if (currentUser?.department === 'electricity') return ['electricity_shutdown'];
    if (currentUser?.department === 'cooperative') return ['water_shutdown', 'electricity_shutdown'];
    return ['water_shutdown', 'electricity_shutdown'];
  }, [currentUser?.department]);

  const getTypeLabel = (type) => (type === 'water_shutdown' ? 'ประปา' : 'ไฟฟ้า');

  const getDepartmentIdByType = useCallback(
    (type) => {
      if (type === 'water_shutdown') {
        const d = departments.find((dept) => /ประปา|น้ำ/i.test(dept.name || ''));
        return d?.id ?? null;
      }
      if (type === 'electricity_shutdown') {
        const d = departments.find((dept) => /ไฟฟ้า/i.test(dept.name || ''));
        return d?.id ?? null;
      }
      return departments[0]?.id ?? null;
    },
    [departments]
  );

  const getDepartmentNameByType = useCallback(
    (type) => {
      const deptId = getDepartmentIdByType(type);
      const d = departments.find((dept) => dept.id === deptId);
      return d?.name ?? '-';
    },
    [departments, getDepartmentIdByType]
  );

  useEffect(() => {
    if (!allowedTypes.includes(formData.type) && allowedTypes[0]) {
      setFormData((prev) => ({ ...prev, type: allowedTypes[0] }));
    }
  }, [allowedTypes, formData.type]);

  useEffect(() => {
    if (!appsUrl) return;
    axios
      .get(appsUrl)
      .then(({ data }) => {
        const list = data?.results ?? (Array.isArray(data) ? data : []);
        setApps(list);
      })
      .catch(() => setApps([]));
  }, [appsUrl]);

  useEffect(() => {
    const appId = selectedAppId;
    const base = appsUrl ? appsUrl.replace(/\/$/, '') : null;
    if (!appId || !base) {
      setDepartments([]);
      return;
    }
    setDepartmentsLoading(true);
    axios
      .get(`${base}/${appId}/departments/`)
      .then(({ data }) => {
        const list = data?.results ?? (Array.isArray(data) ? data : []);
        setDepartments(list.map((d) => ({ id: d.id, name: d.name || '', description: d.description || '' })));
      })
      .catch(() => setDepartments([]))
      .finally(() => setDepartmentsLoading(false));
  }, [appsUrl, selectedAppId]);

  useEffect(() => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return;
    const url = getApiUrl('/api/user/auth/verify/');
    axios
      .get(url, { headers: { Authorization: `Token ${token}` } })
      .then(({ data }) => setAuthVerifyUser(data))
      .catch(() => setAuthVerifyUser(null));
  }, []);

  // Edit: sync form from location.state
  useEffect(() => {
    if (isCreate || !notificationFromState) return;
    const n = notificationFromState;
    const fallback = toDateTimeLocalValue(n?.shutdownDate);
    setFormData({
      type: n?.type || allowedTypes[0] || '',
      message: n?.message || '',
      operation_start: toDateTimeLocalValue(n?.operation_start) || fallback,
      operation_end: toDateTimeLocalValue(n?.operation_end) || fallback,
      display_start: toDateTimeLocalValue(n?.display_start) || fallback,
      display_end: toDateTimeLocalValue(n?.display_end) || fallback,
      attachments: Array.isArray(n?.attachments)
        ? n.attachments.map((a) => ({
            id: a.id,
            file: a.file,
            name: a.name || (a.file ? String(a.file).split('/').pop() : `ไฟล์ ${a.id}`),
            mimeType: a.mimeType || 'application/octet-stream',
            size: a.size,
          }))
        : [],
    });
  }, [id, isCreate, notificationFromState, allowedTypes]);

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error || new Error('File read error'));
      reader.readAsDataURL(file);
    });

  const removeAttachment = async (attachment) => {
    const attId = attachment?.id;
    const isFromApi = typeof attId === 'number' || (typeof attId === 'string' && /^\d+$/.test(attId));
    if (isFromApi && attachmentsUrl) {
      try {
        await axios.delete((attachmentsUrl || '').replace(/\/$/, '') + `/${attId}/`);
        setSnackbarMessage({ severity: 'success', title: 'ลบไฟล์แนบแล้ว', text: '' });
        setSnackbarOpen(true);
      } catch (err) {
        setSnackbarMessage({ severity: 'error', title: 'ลบไฟล์แนบไม่สำเร็จ', text: err?.response?.data?.detail || err?.message || '' });
        setSnackbarOpen(true);
        return;
      }
    }
    setFormData((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((a) => a?.id !== attId),
    }));
  };

  const handleAddAttachments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const current = Array.isArray(formData.attachments) ? formData.attachments : [];
    let total = current.reduce((sum, a) => sum + Number(a?.size || 0), 0);

    if (!isCreate && id && attachmentsUrl) {
      for (const f of files) {
        if (!f) continue;
        if (f.size > MAX_SINGLE_ATTACHMENT_BYTES) {
          setSnackbarMessage({ severity: 'warning', title: 'ไฟล์ใหญ่เกินไป', text: `ไฟล์ "${f.name}" จำกัด ${formatBytes(MAX_SINGLE_ATTACHMENT_BYTES)} ต่อไฟล์` });
          setSnackbarOpen(true);
          continue;
        }
        if (total + f.size > MAX_TOTAL_ATTACHMENT_BYTES) {
          setSnackbarMessage({ severity: 'warning', title: 'ไฟล์แนบรวมใหญ่เกินไป', text: `รวมได้ไม่เกิน ${formatBytes(MAX_TOTAL_ATTACHMENT_BYTES)}` });
          setSnackbarOpen(true);
          break;
        }
        try {
          const fd = new FormData();
          fd.append('announcement', id);
          fd.append('file', f, f.name || 'file');
          fd.append('name', f.name || 'file');
          const { data } = await axios.post((attachmentsUrl || '').replace(/\/$/, '') + '/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const att = {
            id: data?.id,
            file: data?.file,
            name: (data?.file || '').split('/').pop() || f.name || `ไฟล์ ${data?.id}`,
            mimeType: f.type || 'application/octet-stream',
            size: f.size,
          };
          total += f.size;
          setFormData((prev) => ({ ...prev, attachments: [...(prev.attachments || []), att] }));
          setSnackbarMessage({ severity: 'success', title: 'แนบไฟล์แล้ว', text: '' });
          setSnackbarOpen(true);
        } catch (err) {
          setSnackbarMessage({ severity: 'error', title: 'แนบไฟล์ไม่สำเร็จ', text: err?.response?.data?.detail || err?.message || '' });
          setSnackbarOpen(true);
        }
      }
      return;
    }

    const next = [];
    for (const f of files) {
      if (!f) continue;
      if (f.size > MAX_SINGLE_ATTACHMENT_BYTES) {
        setSnackbarMessage({ severity: 'warning', title: 'ไฟล์ใหญ่เกินไป', text: `ไฟล์ "${f.name}" จำกัด ${formatBytes(MAX_SINGLE_ATTACHMENT_BYTES)}` });
        setSnackbarOpen(true);
        continue;
      }
      if (total + f.size > MAX_TOTAL_ATTACHMENT_BYTES) {
        setSnackbarMessage({ severity: 'warning', title: 'ไฟล์แนบรวมใหญ่เกินไป', text: `รวมได้ไม่เกิน ${formatBytes(MAX_TOTAL_ATTACHMENT_BYTES)}` });
        setSnackbarOpen(true);
        break;
      }
      try {
        const dataUrl = await readFileAsDataUrl(f);
        next.push({
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          name: f.name,
          mimeType: f.type || 'application/octet-stream',
          size: f.size,
          dataUrl,
          file: f,
        });
        total += f.size;
      } catch (err) {
        setSnackbarMessage({ severity: 'error', title: 'อ่านไฟล์ไม่สำเร็จ', text: `ไม่สามารถอ่านไฟล์ "${f.name}" ได้` });
        setSnackbarOpen(true);
      }
    }
    if (next.length > 0) {
      setFormData((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...next] }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.type || !formData.message) {
      setSnackbarMessage({ severity: 'warning', title: 'ข้อมูลไม่ครบ', text: 'กรุณากรอกข้อมูลให้ครบถ้วน' });
      setSnackbarOpen(true);
      return;
    }
    if (!formData.operation_start || !formData.operation_end || !formData.display_start || !formData.display_end) {
      setSnackbarMessage({ severity: 'warning', title: 'ข้อมูลไม่ครบ', text: 'กรุณากรอกวันที่และเวลาทั้ง 4 ช่อง' });
      setSnackbarOpen(true);
      return;
    }
    const appId = selectedAppId != null ? Number(selectedAppId) : apps[0]?.id;
    const departmentId = getDepartmentIdByType(formData.type);
    const createUrl = (announcementsUrl || '').replace(/\/$/, '') + '/';
    const updateUrl = (announcementsUrl || '').replace(/\/$/, '') + `/${id}/`;
    if (!selectedAppId || appId == null || Number.isNaN(appId)) {
      setSnackbarMessage({ severity: 'warning', title: 'ข้อมูลไม่ครบ', text: 'กรุณาเลือกแอปที่หน้าหลัก' });
      setSnackbarOpen(true);
      return;
    }
    if (departmentId == null) {
      setSnackbarMessage({ severity: 'warning', title: 'ข้อมูลไม่ครบ', text: 'ไม่พบหน่วยงานที่ตรงกับประเภท' });
      setSnackbarOpen(true);
      return;
    }
    const operation_start = new Date(formData.operation_start).toISOString();
    const operation_end = new Date(formData.operation_end).toISOString();
    const display_start = new Date(formData.display_start).toISOString();
    const display_end = new Date(formData.display_end).toISOString();
    const createdById =
      (currentUser?.id != null ? Number(currentUser.id) : null) ??
      (authVerifyUser?.id != null ? Number(authVerifyUser.id) : null) ??
      (authVerifyUser?.pk != null ? Number(authVerifyUser.pk) : null);
    if (createdById == null || Number.isNaN(createdById)) {
      setSnackbarMessage({ severity: 'warning', title: 'ไม่พบผู้ใช้', text: 'กรุณาเข้าสู่ระบบใหม่' });
      setSnackbarOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      if (!isCreate && id) {
        await axios.put(updateUrl, {
          title: formData.message.slice(0, 100) || 'ประกาศ',
          description: formData.message,
          app: appId,
          department: departmentId,
          operation_start,
          operation_end,
          display_start,
          display_end,
          status: 'active',
          created_by: createdById,
        });
        navigate('/notifications', { state: { message: 'แก้ไขรายการเรียบร้อย', severity: 'success' } });
      } else {
        const { data: ann } = await axios.post(createUrl, {
          title: formData.message.slice(0, 100) || 'ประกาศ',
          description: formData.message,
          app: appId,
          department: departmentId,
          operation_start,
          operation_end,
          display_start,
          display_end,
          status: 'active',
          created_by: createdById,
        });
        const annId = ann?.id;
        if (annId != null && attachmentsUrl) {
          const pending = (formData.attachments || []).filter((a) => a?.file instanceof File);
          for (const a of pending) {
            const fd = new FormData();
            fd.append('announcement', annId);
            fd.append('file', a.file, a.name || 'file');
            fd.append('name', a.name || a.file?.name || 'file');
            await axios.post((attachmentsUrl || '').replace(/\/$/, '') + '/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          }
        }
        navigate('/notifications', { state: { message: 'สร้างรายการเรียบร้อย', severity: 'success' } });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'เกิดข้อผิดพลาด';
      setSnackbarMessage({ severity: 'error', title: isCreate ? 'สร้างไม่สำเร็จ' : 'บันทึกไม่สำเร็จ', text: String(msg) });
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const title = isCreate ? 'สร้างประกาศ' : 'แก้ไขประกาศ';
  const submitLabel = isCreate ? 'สร้าง' : 'บันทึก';

  if (!isCreate && !notificationFromState) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2, width: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'rgba(0, 0, 0, 0.65)' }}>แก้ไขประกาศ</Typography>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/notifications')} sx={{ borderRadius: 2, color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.45)', '&:hover': { borderColor: 'rgba(132, 94, 194, 0.65)' } }}>
            กลับ
          </Button>
        </Box>
        <Alert severity="info" sx={{ width: '100%', maxWidth: 720 }}>
          ไม่พบข้อมูลประกาศ กรุณาเลือกแก้ไขจากหน้ารายการ
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, width: '100%' }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/notifications')} sx={{ borderRadius: 2, color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.45)', '&:hover': { borderColor: 'rgba(132, 94, 194, 0.65)' } }}>
          กลับ
        </Button>
      </Box>
      <Card elevation={0} sx={{ width: '100%', maxWidth: 720, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.05)', bgcolor: 'rgba(255, 255, 255, 0.9)', overflow: 'hidden' }}>
        <Box
          sx={{
            background: 'linear-gradient(to right, #8E6CEB, #4092E9)',
            px: 2,
            pt: 1.5,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
          }}
        >
          
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', flex: 1 }}>
            {title}
          </Typography>
        </Box>
        <CardContent sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 1.5 }}>
            <InputLabel id="ann-type-label">ประเภท</InputLabel>
            <Select labelId="ann-type-label" label="ประเภท" value={formData.type} onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}>
              {allowedTypes.map((t) => (
                <MenuItem key={t} value={t}>{getTypeLabel(t)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="แอปพลิเคชัน"
            value={(() => {
              const appId = selectedAppId != null ? Number(selectedAppId) : apps[0]?.id;
              const app = apps.find((a) => a.id === appId);
              return app?.name ?? (appId ? `ID: ${appId}` : '-');
            })()}
            InputProps={{ readOnly: true }}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            label="หน่วยงาน"
            value={departmentsLoading ? 'กำลังโหลด...' : getDepartmentNameByType(formData.type) || '-'}
            InputProps={{
              readOnly: true,
              endAdornment: departmentsLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ mb: 2 }}
          />
          <TextField fullWidth label="ข้อความ" value={formData.message} onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))} multiline rows={4} sx={{ mb: 2 }} />
          <TextField fullWidth label="เริ่มดำเนินการ" type="datetime-local" value={formData.operation_start} onChange={(e) => setFormData((prev) => ({ ...prev, operation_start: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }} />
          <TextField fullWidth label="สิ้นสุดดำเนินการ" type="datetime-local" value={formData.operation_end} onChange={(e) => setFormData((prev) => ({ ...prev, operation_end: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }} />
          <TextField fullWidth label="เริ่มแสดง" type="datetime-local" value={formData.display_start} onChange={(e) => setFormData((prev) => ({ ...prev, display_start: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }} />
          <TextField fullWidth label="สิ้นสุดแสดง" type="datetime-local" value={formData.display_end} onChange={(e) => setFormData((prev) => ({ ...prev, display_end: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(0,0,0,0.65)', mb: 1 }}>ไฟล์แนบ</Typography>
            <Button component="label" variant="outlined" startIcon={<AttachFileIcon />} sx={{ borderRadius: 2, mb: 1 }}>
              แนบไฟล์/รูป
              <input hidden type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" onChange={async (e) => { await handleAddAttachments(e.target.files); e.target.value = ''; }} />
            </Button>
            {Array.isArray(formData.attachments) && formData.attachments.length > 0 ? (
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                {formData.attachments.map((a) => {
                  const isImage = String(a?.mimeType || '').startsWith('image/');
                  return (
                    <Grid item xs={12} key={a.id || a.name}>
                      <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2, p: 1, bgcolor: 'rgba(255,255,255,0.6)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'rgba(0,0,0,0.65)' }}>{getAttachmentIcon(a?.mimeType)}</Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'rgba(0,0,0,0.75)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a?.name || 'ไฟล์แนบ'}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.55)' }}>{formatBytes(a?.size || 0)}</Typography>
                          </Box>
                          <IconButton size="small" component="a" href={a?.dataUrl || a?.file} download={a?.dataUrl ? (a?.name || 'attachment') : undefined} target={a?.file && !a?.dataUrl ? '_blank' : undefined} rel="noopener noreferrer" sx={{ borderRadius: 2 }} title="ดาวน์โหลด/เปิด">
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => removeAttachment(a)} sx={{ borderRadius: 2 }} title="ลบไฟล์แนบ">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {isImage && (a?.dataUrl || a?.file) && (
                          <Box component="img" alt={a?.name || 'attachment'} src={a.dataUrl || a.file} sx={{ mt: 1, width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.06)' }} />
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'rgba(0,0,0,0.55)' }}>ยังไม่มีไฟล์แนบ</Typography>
            )}
          </Box>
        </CardContent>
        <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/notifications')} disabled={submitting} startIcon={<ArrowBackIcon />} sx={{ borderRadius: 2, borderColor: 'rgba(132, 94, 194, 0.45)', color: '#845EC2', '&:hover': { borderColor: 'rgba(132, 94, 194, 0.65)', bgcolor: 'rgba(132, 94, 194, 0.08)' } }}>
            กลับ
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting} sx={{ bgcolor: '#845EC2', color: 'white', borderRadius: 2, '&:hover': { bgcolor: '#6344A4' } }}>
            {submitting ? 'กำลังบันทึก...' : submitLabel}
          </Button>
        </Box>
      </Card>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarMessage.severity} sx={{ width: '100%' }}>
          {snackbarMessage.text ? `${snackbarMessage.title}: ${snackbarMessage.text}` : snackbarMessage.title}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationFormPage;
